import React from "react";
import { ReviewStars } from "../review-stars";
import { FeefoVerifiedIcon, IcelandVerifiedIcon, ThirdPartyVerifiedIcon } from "../icons";
import { Stack, Text, Flex, Box, Center, Button } from "../../vanilla";
import { getFormattedDate } from "../../utils/utils";
import { IReview } from ".";

interface reviewSummaryProps {
  pages: number,
  count: number
}

type ReviewDisplayProps = {
  reviews: IReview[]
  isLoading: boolean
  reviewSummary: reviewSummaryProps
  loadReviews: () => void
  page: number
  FEEFO_API_PAGE_SIZE: number
}

const ReviewDisplay = ({reviews, isLoading, reviewSummary, loadReviews, page, FEEFO_API_PAGE_SIZE}: ReviewDisplayProps) => {
  const ShowReviewLogo = (reviewObject: IReview) => {
    switch (reviewObject?.products?.[0]?.feedbackVerificationState) {
      case 'feefoVerified':
        return <FeefoVerifiedIcon boxSize="44px" />
      case 'merchantVerified':
        return <IcelandVerifiedIcon boxSize="44px" />
      case 'incentivised':
        return <ThirdPartyVerifiedIcon boxSize="44px" />
      default:
        return null
    }
  }

  const FeefoReviewSkeleton = () => {
    return (
      <Stack gap="16px" borderTop="1px" padding="20px" borderColor="gray100">
        <Stack gap="2px">
          <Stack direction="row" justifyContent="space-between">
            <Stack gap="4px">
              <Box height="20px" width="100px" bg="gray100" />
              <Box bg="gray100" style={{width: 280, height: 18}} />
            </Stack>
            <Flex>
              <Box width="48px" height="28px" bg="gray100" />
            </Flex>
          </Stack>
        </Stack>
        <Flex>
          <Box width="full" height="24px" bg="gray100" style={{maxWidth: 550}} />
        </Flex>
      </Stack>
    )
  }

  return (
    <>
    {/* Reviews Section */}
      {reviews?.map((reviewObject, idx) => {
        const {products, customer, last_updated_date} = reviewObject
        const displayName = customer && customer?.display_name
        const reviewDetail = products[0]
        const {rating, review} = reviewDetail
        return (
          <Stack key={idx} gap="12px" borderTop="1px" padding="20px" borderColor="gray100">
            {/* Review Information */}
            <Stack gap="2px">
              <Stack direction="row" justifyContent="space-between">
                {/* Star Rating & Review */}
                <Stack gap="0px">
                  <ReviewStars rating={rating.rating} />
                  <Text variant="text1" color="textMuted">
                    Submitted {displayName ? `by ${displayName}` : ''} on {`${getFormattedDate(last_updated_date)}`}
                  </Text>
                </Stack>

                {/* Review Badge */}
                <Flex>
                    {ShowReviewLogo(reviewObject)}
                </Flex> 
              </Stack>
            </Stack>

            {/* Review Text */}
            <Flex>
              <Text variant="text3">{review}</Text>
            </Flex>

            {/* Review Disclaimer */}
            {reviewDetail?.feedbackVerificationState == "incentivised" && (
            <Box border="1px" borderRadius="base" padding="12px" gap="10px" borderColor="gray100">
              <Text variant="text1">
                This review has been submitted and independently verified by a third party. This product review may come from a confirmed purchase, free product testing, or be incentivised.
              </Text>
            </Box>
            )}
          </Stack>
        )
      })}

      {/* Show More Reviews Section */}
      {isLoading && Array(FEEFO_API_PAGE_SIZE).fill(0).map((_, idx) => <FeefoReviewSkeleton key={'feefo-review-skeleton-' + idx}/>)}
      <Box padding="20px" borderTop="1px" borderColor="gray100">
        <Center>
          {reviewSummary.count > 0 && (
            <Button
              variant="outlineDark"
              data-test-selector="show-more-reviews-button"
              onClick={() => loadReviews()}
              disabled={page > reviewSummary.pages || isLoading}
            >
              Show More Reviews
            </Button>
          )}
        </Center>
      </Box>
    </>
  )
}

export default ReviewDisplay